// 837P — the professional claim file GAMMIS pays from.
//
// This replaces generate837P() in pages/finance/BillingRun.js, which was
// written without the companion guide, never sent anywhere, and wrong in 25
// separate ways. The full list is in docs/GAMMIS_EDI_INTEGRATION_SPEC.md §4.
// The ones worth repeating here because they would silently misbill rather
// than cleanly reject:
//
//   • Units were sent as whole hours. GAMMIS bills S9123 and S9122 in
//     15-minute units — remittance 13512807 carries 25 units on a single day,
//     which cannot be hours. Sending 4 where GAMMIS expects 16 pays a quarter
//     of the claim. `claim_lines.units` already holds hours x 4 correctly on
//     every line; this reads that column rather than recomputing from hours.
//   • Place of service defaulted to 11 (Office). Home nursing is 12 (Home),
//     which is what every paid line on every remittance shows.
//   • `diagnosis_code` holds a comma-separated list, and the old code both
//     crammed the whole list into one element and stripped only the first
//     decimal point with String.replace. Here the list is split into a
//     principal (ABK) and up to eleven others (ABF).
//
// Nothing here defaults a value that belongs to AHFC or to a claim. A missing
// diagnosis or authorisation is an error raised by the pre-flight check, not a
// placeholder quietly filed to Medicaid.

import {
  seg, transaction, functionalGroup, interchange, d8, t4,
  GAMMIS_TRADING_PARTNER_ID,
} from './x12.js';
import { requireIdentity } from './billingIdentity.js';

export const IMPLEMENTATION_REF = '005010X222A1';

/** GAMMIS is both the receiver (1000B) and the payer (2010BB). */
export const GAMMIS_NAME = 'GEORGIA HEALTH PARTNERSHIP';

/** Home. Every paid nursing line on every AHFC remittance carries this. */
export const PLACE_OF_SERVICE_HOME = '12';

/** 1 = original. 7 = replacement of a paid claim, 8 = void; both need REF*F8. */
export const FREQUENCY_ORIGINAL = '1';

/** ICD-10 qualifiers. ICD-9 (BK/BF) cannot arise — those dates are long past. */
const DX_PRINCIPAL = 'ABK';
const DX_OTHER     = 'ABF';

/** GAMMIS accepts up to 50 service lines per claim; we send one. */
const MAX_DIAGNOSES = 12;

/**
 * Split the stored diagnosis string into EDI-ready codes.
 *
 * `claim_lines.diagnosis_code` holds a comma-separated list — 467 of 782 lines
 * since August carry more than one — and EDI wants the decimal point removed
 * from each code individually.
 */
export const diagnosisCodes = (raw) => {
  const seen = new Set();
  const out = [];
  for (const part of String(raw ?? '').split(/[,;]/)) {
    const code = part.trim().replace(/\./g, '').toUpperCase();
    if (!code || seen.has(code)) continue;
    seen.add(code);
    out.push(code);
    if (out.length === MAX_DIAGNOSES) break;
  }
  return out;
};

/** HI segment: principal as ABK, the rest as ABF. */
const diagnosisSegment = (codes) => seg(
  'HI',
  [DX_PRINCIPAL, codes[0]],
  ...codes.slice(1).map((c) => [DX_OTHER, c]),
);

const money = (n) => (Math.round((Number(n) || 0) * 100) / 100).toFixed(2);

/**
 * One claim_lines row plus its client, as the generator wants it.
 *
 * Kept separate from the builder so the database shape is mapped in one place
 * and the EDI structure can be tested without inventing database rows.
 */
export const claimFromLine = (line, client) => ({
  // CLM01 is echoed back on the 835 (837P CG §2300) and on a claim-level 824
  // rejection, so a deterministic value from the line id turns remittance
  // matching from a heuristic into an exact lookup.
  //
  // CLM01 allows 38 characters and a hyphen-stripped uuid is 32, so the whole
  // id goes. Truncating would drop the tail, and two rows differing only there
  // would then share a claim number — the one thing this value must never do.
  controlNumber: String(line.id || '').replace(/-/g, '').slice(0, 38).toUpperCase(),
  serviceDate:   line.service_date,
  serviceCode:   String(line.service_code || '').trim().toUpperCase(),
  modifier:      String(line.modifier || '').trim().toUpperCase(),
  units:         Math.round(Number(line.units) || 0),
  chargeAmount:  Number(line.charge_amount) || 0,
  diagnoses:     diagnosisCodes(line.diagnosis_code),
  priorAuth:     String(line.prior_auth_number || '').trim(),
  placeOfService: String(line.place_of_service || PLACE_OF_SERVICE_HOME),
  client: {
    medicaidId: String(client?.medicaid_id || line.medicaid_id || '').replace(/\s/g, ''),
    firstName:  String(client?.first_name || '').trim(),
    lastName:   String(client?.last_name || '').trim(),
    dob:        client?.date_of_birth || null,
    gender:     String(client?.gender || '').trim().toUpperCase().slice(0, 1) || 'U',
    address1:   String(client?.address || '').trim(),
    city:       String(client?.city || '').trim(),
    state:      String(client?.state || '').trim(),
    zip:        String(client?.zip || '').replace(/\D/g, ''),
  },
});

/** Group claims by subscriber — one 2000B hierarchical level each. */
const bySubscriber = (claims) => {
  const groups = new Map();
  for (const c of claims) {
    const key = c.client.medicaidId || `${c.client.lastName},${c.client.firstName}`;
    if (!groups.has(key)) groups.set(key, { client: c.client, claims: [] });
    groups.get(key).claims.push(c);
  }
  return [...groups.values()];
};

/**
 * Build the 837P body — everything between ST and SE.
 *
 * Exported for testing: the envelope is exercised separately in the x12 suite,
 * and asserting on the body alone keeps those tests readable.
 */
export const build837PBody = ({ identity, claims, at, batchRef }) => {
  const body = [];
  const push = (...s) => body.push(...s);

  push(seg('BHT', '0019', '00', batchRef, d8(at), t4(at), 'CH'));

  // 1000A submitter / 1000B receiver. The submitter is identified by the
  // trading partner ID, not the NPI — the NPI identifies the billing provider
  // in 2010AA, which is a different role even when it is the same company.
  push(
    seg('NM1', '41', '2', identity.legalName, '', '', '', '', '46', identity.tradingPartnerId),
    // P0304: if either PER03 or PER04 is present, both must be. A qualifier
    // with no number behind it fails SNIP 1 on the whole interchange, so an
    // absent phone drops the pair rather than emitting half of it. The
    // identity check refuses transmission without one anyway — this is the
    // belt to that braces.
    identity.phone
      ? seg('PER', 'IC', 'BILLING', 'TE', identity.phone)
      : seg('PER', 'IC', 'BILLING'),
    seg('NM1', '40', '2', GAMMIS_NAME, '', '', '', '', '46', GAMMIS_TRADING_PARTNER_ID),
  );

  // 2000A billing provider.
  push(
    seg('HL', '1', '', '20', '1'),
    seg('PRV', 'BI', 'PXC', identity.taxonomy),
    seg('NM1', '85', '2', identity.legalName, '', '', '', '', 'XX', identity.npi),
    seg('N3', identity.address1, identity.address2),
    seg('N4', identity.city, identity.state, identity.zip),
    seg('REF', 'EI', identity.ein),
  );

  let hl = 1;
  for (const { client, claims: subscriberClaims } of bySubscriber(claims)) {
    hl += 1;
    // HL04 = 0: no dependent level follows. A 2000C patient loop fails GAMMIS
    // compliance outright (CG §7.2), and cannot arise here — under Medicaid
    // the child is the subscriber.
    push(
      seg('HL', String(hl), '1', '22', '0'),
      // SBR09 = MC (Medicaid). It is the ninth element, so the empties before
      // it have to be present; the previous generator supplied eight and put
      // the claim filing indicator in SBR08.
      seg('SBR', 'P', '18', '', '', '', '', '', '', 'MC'),
      seg('NM1', 'IL', '1', client.lastName, client.firstName, '', '', '', 'MI', client.medicaidId),
      seg('N3', client.address1),
      seg('N4', client.city, client.state, client.zip),
      seg('DMG', 'D8', d8(client.dob), client.gender),
      seg('NM1', 'PR', '2', GAMMIS_NAME, '', '', '', '', 'PI', GAMMIS_TRADING_PARTNER_ID),
    );

    for (const claim of subscriberClaims) {
      push(seg(
        'CLM', claim.controlNumber, money(claim.chargeAmount), '', '',
        [claim.placeOfService, 'B', FREQUENCY_ORIGINAL],
        'Y', 'A', 'Y', 'I',
      ));
      // ORDER MATTERS AND IS NOT OBVIOUS. In loop 2300 the TR3 puts REF at
      // guideline position 1800 and HI at 2310, so the prior authorisation
      // comes BEFORE the diagnoses. Emitting them the other way round is not
      // a warning — Edifecs rejects every claim in the file with "Extra data
      // was encountered ... the last known Segment was HI", because once HI
      // has been seen a REF can no longer belong to the loop.
      if (claim.priorAuth) push(seg('REF', 'G1', claim.priorAuth));
      if (claim.diagnoses.length) push(diagnosisSegment(claim.diagnoses));

      // 2310B rendering provider is omitted deliberately: AHFC bills and
      // renders under the same NPI, and the TR3 says to send the loop only
      // when the rendering provider differs from the billing provider.
      push(
        seg('LX', '1'),
        seg('SV1',
          ['HC', claim.serviceCode, claim.modifier],
          money(claim.chargeAmount), 'UN', String(claim.units), '', '', '1'),
        seg('DTP', '472', 'D8', d8(claim.serviceDate)),
      );
    }
  }

  return body;
};

/**
 * A complete 837P interchange.
 *
 * `usageIndicator` is 'T' until GAMMIS certifies the trading partner and 'P'
 * after — it is a parameter rather than a constant because sending production
 * files during certification, or test files after it, are both real mistakes.
 *
 * `controlNumber` is required and comes from the database counter — see the
 * note on control numbers in x12.js. It is used for ISA13, GS06 and ST02.
 */
export const build837P = ({
  identity,
  claims,
  at = new Date(),
  usageIndicator = 'T',
  controlNumber,
  batchRef,
}) => {
  requireIdentity(identity, { forTransmission: true });
  if (!claims?.length) throw new Error('No claim lines to send.');

  const body = build837PBody({
    identity, claims, at,
    batchRef: batchRef || `BR${d8(at)}${t4(at)}`,
  });

  return interchange({
    senderId: identity.tradingPartnerId,
    controlNumber,
    usageIndicator,
    at,
    groups: [functionalGroup({
      functionalId: 'HC',
      senderId: identity.tradingPartnerId,
      controlNumber,
      version: IMPLEMENTATION_REF,
      at,
      transactions: [transaction({
        code: '837',
        controlNumber,
        implementationRef: IMPLEMENTATION_REF,
        body,
      })],
    })],
  });
};
